import { datadogRum } from '@datadog/browser-rum';
import { AnswerStateSchema } from '@repo/contracts/cardfinder/funnel/schema';
import type { AnswerState, AnswerValue } from '@repo/contracts/cardfinder/funnel/types';
import * as v from 'valibot';
import {
  FUNNEL_SESSION_STATUSES,
  type FunnelSession,
  SAVED_AS_VALUES,
} from '@/entities/session/model/types';
import { readSession, removeFromSession, writeSession } from '@/shared/lib/sessionStorage';

const FUNNEL_SESSION_KEY = 'session';
const INPUT_KEY = 'answers';

const FunnelSessionSchema = v.object({
  answers: AnswerStateSchema,
  status: v.picklist(FUNNEL_SESSION_STATUSES),
  sessionId: v.nullable(v.string()),
  savedAs: v.nullable(v.picklist(SAVED_AS_VALUES)),
});

export function loadFunnelSession(): FunnelSession | undefined {
  const stored = readSession<unknown>(FUNNEL_SESSION_KEY);
  if (stored === undefined) return undefined;

  const parsed = v.safeParse(FunnelSessionSchema, stored);
  if (!parsed.success) {
    datadogRum.addError(new Error('Stored funnel session is invalid'), {
      issues: v.flatten(parsed.issues),
    });
    removeFromSession(FUNNEL_SESSION_KEY);
    return undefined;
  }
  return parsed.output;
}

export function writeFunnelSession(session: FunnelSession): void {
  writeSession<FunnelSession>(FUNNEL_SESSION_KEY, session);
}

/** Reads the in-progress input, which lives apart from the funnel session so a reload keeps unsubmitted picks. */
export function loadInput(): AnswerState | undefined {
  const stored = readSession<unknown>(INPUT_KEY);
  if (stored === undefined) return undefined;

  const parsed = v.safeParse(AnswerStateSchema, stored);
  if (!parsed.success) {
    datadogRum.addError(new Error('Stored funnel input is invalid'), {
      issues: v.flatten(parsed.issues),
    });
    removeFromSession(INPUT_KEY);
    return undefined;
  }
  return parsed.output;
}

/** Replaces the stored input; the provider calls it on load and when a reset replaces the input. */
export function writeInput(input: AnswerState): void {
  writeSession<AnswerState>(INPUT_KEY, input);
}

/** Sets one step's answer on the latest stored input, so it follows any write made just before. */
export function writeInputAnswer(stepId: string, value: AnswerValue): void {
  writeInput({ ...loadInput(), [stepId]: value });
}

/** Removes one step's answer from the stored input; the storage counterpart of `clearAnswer`. */
export function removeInputAnswer(stepId: string): void {
  const { [stepId]: _removed, ...rest } = loadInput() ?? {};
  writeInput(rest);
}
